import { useCallback } from 'react';
import { NotifiableError } from '@bugsnag/js';
import {
    DESIGN_LIVE_REVIEW_METHOD,
    bugtracker,
    hasCollaborationMethodOption,
} from '@99designs/design-services-common';
import { FulfilmentStrategy, KeyValue } from '@99designs/graph-utils/types';
import { __url } from '@99designs/i18n';
import { BRIEF_PAGE_NAME, sendAddToCartTrackingEvent } from '@99designs/tracking';
import { useDesignLiveAppointmentUpdatedMutation, useSubmitRequestMutation } from '../../graph';
import { OrphanedBriefError } from '../../lib/errors';
import { RequestExperiments, hasOverflowFeatureFlag } from '../../lib/experiments';
import { UpsertToCartRequest, useCart } from '../../lib/hooks';
import { buildDesignLiveAppointmentInput } from '../BriefForm/FormFields/ClientConfiguredFields/DesignLiveAppointmentV1/appointmentValue';
import { BriefRequestFragment } from '../BriefForm/brief.generated';
import { DesignServiceRequestType, MERCHANT_SERVICE_CONFIG } from './consts';
import { getWorkIdFromDynamicFields } from './getWorkIdFromDynamicFields';
import { designLiveDataFromBrief, getRequestType } from './requestDataFromBrief';
import { BriefType } from './useHandleSubmit';

export const useSubmitToRequests = (
    locale: string,
    authorizationHeader: string,
    shopperId: string,
    briefId: string
) => {
    const [designLiveAppointmentUpdated] = useDesignLiveAppointmentUpdatedMutation();
    const [submitRequest] = useSubmitRequestMutation();
    const { upsertToCart, requestIdByBriefId: requestIdByBriefIdFromCart } = useCart(locale, {
        authorizationHeader,
        shopperId,
    });

    return useCallback(
        async (data: BriefType) => {
            try {
                const request = data.request;

                if (!request) {
                    throw new OrphanedBriefError(
                        `Encountered orphaned brief in useSubmitToRequests hook ${briefId}`
                    );
                }

                const productSupportsCollabMethod = hasCollaborationMethodOption(
                    request.facts?.serviceProductOptions
                );

                const productData = formatProductData(data);

                const expertProductPrd = productData.experience
                    ? carePrdForCart(
                          productData.experience,
                          productData.cimpressProductKey,
                          productSupportsCollabMethod
                      )
                    : productData.cimpressProductKey;
                const productDataForCart = { ...productData, expertProductPrd };

                if (shouldUpdateDesignLiveAppointment(productDataForCart)) {
                    await updateDesignLiveAppointment(
                        productDataForCart,
                        designLiveAppointmentUpdated,
                        request
                    );
                }

                const cartCorrelationId = await requestIdByBriefIdFromCart(briefId);
                if (cartCorrelationId.length === 0) {
                    await submitRequestAndTrack(submitRequest, request, productDataForCart);
                }

                await submitToCart(productDataForCart, briefId, request, data, upsertToCart);

                return;
            } catch (e) {
                bugtracker.notify(e as NotifiableError, (event) => {
                    event.context = `Error during submission to requests for: ${briefId}.`;
                });
                throw e;
            }
        },
        [
            requestIdByBriefIdFromCart,
            briefId,
            upsertToCart,
            designLiveAppointmentUpdated,
            submitRequest,
        ]
    );
};

type ProductDataForCart = ReturnType<typeof formatProductData> & { expertProductPrd: string };

export function shouldUpdateDesignLiveAppointment(productData: ProductDataForCart) {
    // Reconcile when appointment data exists.
    return (
        productData.experience === DESIGN_LIVE_REVIEW_METHOD &&
        Boolean(
            productData.briefDesignLiveData.designLiveAppointmentTime &&
                productData.briefDesignLiveData.timezone
        )
    );
}

export async function updateDesignLiveAppointment(
    productData: ProductDataForCart,
    designLiveAppointmentUpdated: ReturnType<typeof useDesignLiveAppointmentUpdatedMutation>[0],
    request: BriefRequestFragment
) {
    if (
        !productData.briefDesignLiveData.timezone ||
        !productData.briefDesignLiveData.designLiveAppointmentTime
    ) {
        throw new Error(
            `design live data is not sufficient to book an appointment: ${JSON.stringify(
                productData.briefDesignLiveData
            )}`
        );
    }
    const { errors } = await designLiveAppointmentUpdated({
        variables: {
            requestId: request.id,
            input: buildDesignLiveAppointmentInput(
                productData.briefDesignLiveData.designLiveAppointmentTime,
                productData.briefDesignLiveData.timezone,
                productData.briefDesignLiveData.calendarId
            ),
        },
    });

    if (errors && errors.length > 0) {
        throw errors;
    }
}

export async function submitRequestAndTrack(
    submitRequest: ReturnType<typeof useSubmitRequestMutation>[0],
    request: BriefRequestFragment,
    productData: ProductDataForCart
) {
    const resp = await submitRequest({
        variables: {
            requestId: request.id,
        },
    });
    if (!resp.data) throw new Error(`Failed to call submit request`);

    sendAddToCartTrackingEvent({
        pageName: BRIEF_PAGE_NAME,
        pageStage: 'Design',
        pageSection: 'Design Services',
        product_id: productData.expertProductPrd, // should be mpvId
        name: productData.productName,
        core_product_id: productData.expertProductPrd,
        price: productData.productPriceInDecimal,
        currency: request.expertService?.price.currency,
    });
}

export function getCartProductData(
    productData: ProductDataForCart,
    briefId: string,
    request: BriefRequestFragment,
    data: BriefType,
    experiments: RequestExperiments,
    optionsForCart: KeyValue[]
) {
    const cartProduct: UpsertToCartRequest['product'] = {
        productKey: productData.expertProductPrd,
        productVersion: productData.cimpressProductVersion,
        productName: productData.productName,
        fulfilmentData: {
            expertRequestID: request.id,
            experiments: experiments.Marshal(),
            // The following fields are required in the order confirmation UX mod:
            productBriefID: briefId,
            postPaymentUrl: __url('/graphic-design/dashboard'),
            printProductMPVId: data.fulfilment?.mpvId || '',
            fulfilmentStrategy: data.fulfilmentStrategy ?? undefined,
            ...(productData.experience
                ? { withDesignLive: productData.experience === DESIGN_LIVE_REVIEW_METHOD }
                : {}),
        },
    };

    // TODO: Disgusting. But necessary for now.
    const fulfilmentSize = data.fulfilment?.size;
    const serviceMpvId = request.expertService?.serviceProduct.mpvId?.toLowerCase() ?? '';
    // TODO: This ProjectSize cart logic is temporary and will move to another service soon.
    // Only careDesign and designLive use ProjectSize
    const shouldIncludeProjectSize =
        fulfilmentSize && (serviceMpvId === 'caredesign' || serviceMpvId === 'designlive');

    if (shouldIncludeProjectSize) {
        cartProduct.selectedAttributes = [
            {
                value: fulfilmentSize,
                key: 'ProjectSize',
            },
        ];
    }

    const serviceProductOptions = sanitizeServiceProductOptions(
        !!request.expertService?.isOverflowEligible,
        optionsForCart,
        experiments.Has('overflow'),
        !!shouldIncludeProjectSize
    );

    if (serviceProductOptions.length > 0) {
        const selectedAttributes = cartProduct.selectedAttributes ?? [];
        cartProduct.selectedAttributes = [...selectedAttributes, ...serviceProductOptions];
    }

    return cartProduct;
}

function sanitizeServiceProductOptions(
    isServiceProductOverflowEligible: boolean,
    options: KeyValue[] | null | undefined,
    overflowEnabled: boolean,
    shouldIncludeFulfilmentSize: boolean
): { key: string; value: string }[] {
    // This function is a temporary convenience, this pseudo option selection is
    // an anti-pattern that we need to address ASAP
    // PLEASE DO NOT ADD TO THIS WITHOUT CHECKING WITH DPI FIRST
    const normalizedOptions = (options ?? []).map(({ key, value }) => ({ key, value }));

    if (shouldIncludeFulfilmentSize) {
        return [];
    }

    if (!isServiceProductOverflowEligible) {
        return normalizedOptions;
    }

    if (isServiceProductOverflowEligible && normalizedOptions.length === 0) {
        // If there are no options but the service is eligible for overflow, we
        // need to add a default option
        normalizedOptions.push({
            key: 'DesignerGroupOption',
            value: 'In-house',
        });
    }

    if (overflowEnabled) {
        return normalizedOptions.map((option) => {
            const shouldChangeToFreelance =
                option.key === 'DesignerGroupOption' && option.value === 'In-house';

            return shouldChangeToFreelance ? { ...option, value: 'Freelance' } : option;
        });
    }

    return normalizedOptions.map(({ key, value }) => ({ key, value }));
}

async function submitToCart(
    productData: ProductDataForCart,
    briefId: string,
    request: BriefRequestFragment,
    data: BriefType,
    upsertToCart: (payload: UpsertToCartRequest) => Promise<void>
) {
    const isOverflowEnabled = await hasOverflowFeatureFlag(
        request.expertService?.isOverflowEligible,
        request.facts?.withDesignLive
    );

    const experiments = new RequestExperiments()
        .WithFlag('useRequests') // useRequests is still required by default.
        .WithFlag('overflow', isOverflowEnabled);

    const optionsForCart = request.facts?.serviceProductOptions ?? [];

    const cartProduct = getCartProductData(
        productData,
        briefId,
        request,
        data,
        experiments,
        optionsForCart
    );

    // NOTE: Can't use requestID as the correlationID until the brief has a requestID on it (or it's the same ID))
    await upsertToCart({
        product: cartProduct,
        correlationId: briefId,
        workId: getWorkIdFromDynamicFields(data.dynamicFields),
    });
}

export function formatProductData(data: BriefType) {
    const product = data.product;
    const expertService = data.request?.expertService;
    const fulfillmentStrategy = data.fulfilmentStrategy ?? product?.fulfilmentStrategy;
    if (!fulfillmentStrategy) {
        throw new Error('Fulfillment strategy is not present in claim structured brief response');
    }
    const productName = expertService?.title ?? product?.title;
    if (!productName) {
        throw new Error('Product name is not present in claim structured brief response');
    }

    const dynamicPriceInDecimal = expertService?.price
        ? expertService.price.listPriceCents.taxed / 100
        : undefined;
    const staticPriceInDecimal = Math.round((product?.pricing.amount.amountInCents ?? 0) / 100);
    const productPriceInDecimal = dynamicPriceInDecimal ?? staticPriceInDecimal;

    if (!productPriceInDecimal) {
        throw new Error('Product price is not present in claim structured brief response');
    }
    const cimpressProductKey =
        expertService?.serviceProduct.productKey ??
        product?.cimpressProductConfig?.cimpressProductKey;
    if (!cimpressProductKey) {
        throw new Error('Cimpress product key is not present in claim structured brief response');
    }
    const cimpressProductVersion =
        expertService?.serviceProduct.pimMerchantVersion ??
        product?.cimpressProductConfig?.cimpressProductVersion;
    if (!cimpressProductVersion) {
        throw new Error(
            'Cimpress product version is not present in claim structured brief response'
        );
    }

    const requestType = getRequestType(data.dynamicFields);

    const collabExperience = getCollabExperience(fulfillmentStrategy, requestType);

    const briefDesignLiveData = designLiveDataFromBrief(data.dynamicFields);

    return {
        fulfillmentStrategy,
        productName,
        productPriceInDecimal,
        cimpressProductKey,
        cimpressProductVersion,
        experience: collabExperience,
        briefDesignLiveData,
    };
}

function getCollabExperience(
    fulfillmentStrategy: FulfilmentStrategy,
    requestType: DesignServiceRequestType | false
): DesignServiceRequestType | false {
    if (fulfillmentStrategy !== 'FULFILMENT_STRATEGY_CARE' || !requestType) {
        return false;
    }

    // Return the experience type for any CARE product with a valid requestType
    // Both legacy and new products can have Standard/DesignLive experiences
    return requestType;
}

function isCareProductKey(productKey: string): boolean {
    return (
        productKey === MERCHANT_SERVICE_CONFIG.Standard ||
        productKey === MERCHANT_SERVICE_CONFIG.DesignLive
    );
}

// TODO: Get the CARE PRD from the parent request!
// Note: this function accounts for the fact that there is one expert product for two PRDs for CARE products
function carePrdForCart(
    careCollaborationExperience: DesignServiceRequestType,
    cimpressProductKey: string,
    productSupportsCollabMethod: boolean
): string {
    // When the product supports CollaborationMethodOption, DesignLive is
    // expressed as a Sync attribute on the cart item rather than switching
    // to a different PRD. Keep the product's own key.
    if (productSupportsCollabMethod) {
        return cimpressProductKey;
    }

    // Only use MERCHANT_SERVICE_CONFIG for legacy CARE products
    // Legacy products use a shared product key for both Standard and DesignLive experiences
    if (isCareProductKey(cimpressProductKey)) {
        return MERCHANT_SERVICE_CONFIG[careCollaborationExperience];
    }
    // For newer products, use their actual product key
    return cimpressProductKey;
}
